import { Box, Button, Divider, Typography } from '@mui/material';
import { IStory } from '@opengov/stories-shared/types';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  ISessionUser,
  IStoriesFolder,
  IStoryListMessages,
  IStoryListSelection
} from '../../../../types';
import { getDisplayNameForNodeId } from '@opengov/component-library/capital';
import { alphabetizeNodes, compileNodes } from '../../../utils';
import {
  useOrganizeStoriesAndFoldersRoute,
  useOrganizeStoriesInFolder,
  useOrganizeStoryFolders,
  useStories,
  useStoryFolders
} from '../../StoryList/hooks';
import { useHasAdminPrivilege } from '../../../hooks/subjectClaims';

import { Sidebar } from '../../StoryList/Sidebar';

import { NavigationBlockerPopup } from '../NavigationBlockerPopup';
import { OrganizeFoldersModal } from '../OrganizeFoldersModal';
import { PageHeader } from '../PageHeader';
import { StoryListTable } from '../StoryListTable';

import { styles } from './OrganizeStoriesAndFolders.styles';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';

export interface IOrganizeStoriesAndFoldersProps {
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  messages: IStoryListMessages;
  displayPublicationTab: boolean;
  user: ISessionUser;
  setSelectedList(
    newList: IStoryListSelection,
    entitySubdomain: string,
    isOrganize: boolean,
    selectedFolderId: string
  ): void;
  pushToFolderList(entitySubdomain: string, folderId: string): void;
}

const getSelectedFolderStructure = (allFolders: IStoriesFolder[], folderId: string): IStoriesFolder[] => {
  const allStoriesFolders = _.cloneDeep(allFolders);
  const selectedFolderStructure = [];
  function searchFolder(folders: IStoriesFolder[], selectedFolderID: string) {
    folders.forEach((folder: IStoriesFolder) => {
      if (folder.id === selectedFolderID) {
        selectedFolderStructure.push(folder);
      } else if (folder?.children?.length) {
        searchFolder(folder.children, selectedFolderID);
      }
    });
  }
  searchFolder(allStoriesFolders, folderId);
  return selectedFolderStructure;
};

const OrganizeStoriesAndFoldersInternal: React.FC<IOrganizeStoriesAndFoldersProps> = (props) => {
  const {
    messages,
    entityId,
    entityUuid,
    entitySubdomain,
    user,
    displayPublicationTab,
    setSelectedList,
    pushToFolderList
  } = props;

  const isAdmin = useHasAdminPrivilege(
    entityUuid && user?.uuid ? { userUuid: user.uuid, entityUuid } : null
  );

  const { rootFolderId: rootId, selectedFolderId, currentFolderId } = useOrganizeStoriesAndFoldersRoute();

  const { data: storyFoldersData } = useStoryFolders(
    entityId > 0 ? { entityId, displayPublicationTab } : null,
    messages.somethingWentWrongError
  );
  const rawStoryFolders = storyFoldersData?.storyFolders ?? [];
  const folderStructure: IStoriesFolder[] = useMemo(
    () => alphabetizeNodes(compileNodes(rawStoryFolders[0]?.subFolders) || []),
    [rawStoryFolders]
  );

  const selectedFolderStructure = getSelectedFolderStructure(folderStructure, rootId);
  const currentFolderName = getDisplayNameForNodeId(currentFolderId, folderStructure);
  const selectedList: IStoryListSelection = {
    id: selectedFolderId ?? '',
    displayName: currentFolderName ?? ''
  };

  const [isOrganizeStoriesInProgress, setIsOrganizeStoriesInProgress] = useState(false);
  const [organizedStoriesList, setOrganizedStoriesList] = useState<IStory[] | null>(null);
  const [selectedFolder, setSelectedFolder] = useState(
    getSelectedFolderStructure(selectedFolderStructure, selectedFolderId)[0] || null
  );
  const [clickedFolder, setClickedFolder] = useState(null);
  const [isOrganizefolderModalOpen, setIsOrganizefolderModalOpen] = useState(false);
  const [showBlockerPopup, setShowBlockerPopup] = useState(false);
  const [navLink, setNavLink] = useState(null);

  const organizeStoriesMutation = useOrganizeStoriesInFolder(
    { title: messages.somethingWentWrongError, body: messages.storyOrganizeFoldersFailed },
    { title: messages.changesSaved, body: messages.changesSavedSuccessfully }
  );

  const organizeFoldersMutation = useOrganizeStoryFolders(
    { title: messages.somethingWentWrongError, body: messages.storyOrganizeFoldersFailed },
    { title: messages.changesSaved, body: messages.changesSavedSuccessfully }
  );

  const storiesQueryParams =
    entityId && isAdmin && selectedFolder?.id
      ? {
        entityId,
        page: 1,
        folderId: selectedFolder.id,
        showAllStoriesForSelectedFolder: true,
        sort: 'custom',
        storyVisibility: 'all' as const,
        ogId: user.id
      }
      : null;

  const storiesQuery = useStories(storiesQueryParams, messages.somethingWentWrongError);
  const storyListContents = storiesQuery.data;
  const isLoading = storiesQuery.isFetching || organizeStoriesMutation.isLoading;

  const handleBackClick = () => {
    setIsOrganizeStoriesInProgress(false);
    const selectedId = location.href.split('/').pop();
    const selected = getSelectedFolderStructure(selectedFolderStructure, selectedId);
    if (selected[0]) {
      setSelectedFolder(selected[0]);
    }
  };

  const handleBrowserNavigation = (event) => {
    if (isOrganizeStoriesInProgress && !navLink && !showBlockerPopup) {
      event.preventDefault();
      event.returnValue = messages.unsavedChangesDescription;
      return '';
    }
  };

  const handleNavigationClick = (event) => {
    if (!isOrganizeStoriesInProgress) return;
    let targetLink;
    if (event.target.tagName === 'A') {
      targetLink = event.target.href;
    } else {
      targetLink = event.target.closest('a')?.href;
    }
    event.preventDefault();
    setNavLink(targetLink);
    setShowBlockerPopup(true);
  };

  useEffect(() => {
    if (!showBlockerPopup && !isOrganizeStoriesInProgress) {
      // Trigger a refetch when folder selection changes or organize completes.
      void storiesQuery.refetch();
    }
    const selected = getSelectedFolderStructure(selectedFolderStructure, selectedFolderId);
    if (selected[0] && !selectedFolder) {
      setSelectedFolder(selected[0]);
    }

    window.addEventListener('beforeunload', handleBrowserNavigation);
    window.document.querySelector('nav')?.addEventListener('click', handleNavigationClick);
    window.addEventListener('popstate', handleBackClick);

    return () => {
      window.removeEventListener('popstate', handleBackClick);
      window.removeEventListener('beforeunload', handleBrowserNavigation);
      window.document.querySelector('nav')?.removeEventListener('click', handleNavigationClick);
    };
  }, [entityId, folderStructure, isOrganizeStoriesInProgress, showBlockerPopup, selectedFolder, selectedFolderId]);

  const setFolderSelection = (folder: IStoriesFolder) => {
    setClickedFolder(folder);
    if (isOrganizeStoriesInProgress) {
      setShowBlockerPopup(true);
      setSelectedFolder(null);
      return;
    }
    setSelectedFolder(getSelectedFolderStructure(selectedFolderStructure, folder.id)[0] || null);
    setSelectedList(folder, entitySubdomain, true, rootId);
  };

  const navigateToNewFolder = () => {
    if (!!navLink) {
      const link = navLink;
      setNavLink(null);
      window.location.href = link;
      return;
    }

    setSelectedFolder(getSelectedFolderStructure(selectedFolderStructure, clickedFolder.id)[0] || null);
    setSelectedList(clickedFolder, entitySubdomain, true, rootId);
    setIsOrganizeStoriesInProgress(false);
    setShowBlockerPopup(false);
  };

  const setPreviousSelectedFolder = () => {
    setSelectedList({ id: selectedFolder.id, displayName: selectedFolder.displayName }, entitySubdomain, true, rootId);
    setNavLink(null);
    setShowBlockerPopup(false);
  };

  const hasChildFolders = () => selectedFolder?.children?.length > 1;

  const renderModal = () => {
    const organizeFoldersModal =
      isOrganizefolderModalOpen &&
      createPortal(
        <OrganizeFoldersModal
          messages={messages}
          selectedFolderId={selectedFolder?.id}
          folderStructure={folderStructure}
          entityId={entityId}
          currentFolderName={selectedFolder?.id}
          organizeStoryFoldersByOrder={organizeFoldersMutation.mutateAsync}
          onClose={() => setIsOrganizefolderModalOpen(false)}
        />,
        document.getElementById('app')
      );
    const navigationBlockerModal = showBlockerPopup && (
      <NavigationBlockerPopup
        messages={messages}
        onConfirmNavigate={navigateToNewFolder}
        onCancel={setPreviousSelectedFolder}
      />
    );
    return [organizeFoldersModal, navigationBlockerModal];
  };

  if (selectedFolderStructure.length) selectedFolderStructure[0].toggled = true;

  const cancelButtonAction = () => {
    setIsOrganizeStoriesInProgress(false);
    void storiesQuery.refetch();
  };

  const saveButtonAction = () => {
    if (organizedStoriesList && organizedStoriesList.length) {
      const orderedStoriesIds = organizedStoriesList.map((childNode) => childNode._id);
      void organizeStoriesMutation.mutateAsync({
        orderedStoriesIds,
        entityId,
        selectedFolderId: currentFolderId,
        currentFolderName
      }).then(() => {
        setIsOrganizeStoriesInProgress(false);
        setOrganizedStoriesList(null);
      });
    } else {
      setIsOrganizeStoriesInProgress(false);
    }
  };
  return (
    <Box sx={styles.root} data-test="organize-stories-folders-page">
      {renderModal()}
      <PageHeader
        messages={messages}
        isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
        pushToFolderList={pushToFolderList}
        entitySubdomain={entitySubdomain}
        folderId={rootId}
      />
      <Box sx={styles.contentWrapper} data-test="organize-stories-folders-content">
        <Sidebar
          currentFolderId={selectedFolder?.id}
          entityId={entityId}
          folderStructure={selectedFolderStructure}
          isAdmin={isAdmin}
          messages={messages}
          rootFolderId={rootId}
          selectedList={selectedList}
          entitySubdomain={entitySubdomain}
          setSelectedList={setFolderSelection}
          isOrganizeStories={true}
        />
        <Box sx={styles.content}>
          <Box sx={styles.contentHeader}>
            <Typography variant="h5" component="h2" data-test="selected-folder-title">
              {selectedFolder?.displayName}
            </Typography>
            <Box sx={styles.buttons}>
              {hasChildFolders() && !!selectedFolder && !!selectedFolder.id && (
                <Button
                  disabled={isOrganizeStoriesInProgress}
                  data-test="organize-folders-button"
                  variant="outlined"
                  size="small"
                  onClick={() => setIsOrganizefolderModalOpen(true)}
                >
                  {messages.organizeFolders}
                </Button>
              )}
              {isOrganizeStoriesInProgress && (
                <>
                  <Button
                    disabled={isLoading}
                    data-test="organize-stories-cancel-button"
                    variant="outlined"
                    size="small"
                    onClick={cancelButtonAction}
                  >
                    {messages.cancel}
                  </Button>
                  <Button
                    disabled={isLoading}
                    data-test="organize-stories-save-button"
                    variant="contained"
                    size="small"
                    onClick={saveButtonAction}
                  >
                    {messages.save}
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box sx={styles.description}>
            <Typography
              color="text.secondary"
              fontWeight="medium"
              variant="body2"
              sx={styles.organizeStoriesText}
            >
              {messages.organizeStories.toUpperCase()}
            </Typography>
            <Typography variant="body2" sx={styles.organizeStoriesDescText}>
              {messages.organizeStoriesDescription}
            </Typography>
          </Box>
          <Divider />
          <Box sx={styles.storiesTable}>
            <StoryListTable
              isLoading={isLoading}
              messages={messages}
              storyListContents={storyListContents}
              isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
              setIsOrganizeStoriesInProgress={setIsOrganizeStoriesInProgress}
              setOrganizedStoriesList={setOrganizedStoriesList}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const OrganizeStoriesAndFolders: React.FC<IOrganizeStoriesAndFoldersProps> = (props) => {
  return (
    <PageErrorBoundary viewName={ViewName.OrganizeStoriesAndFoldersPage}>
      <OrganizeStoriesAndFoldersInternal {...props} />
    </PageErrorBoundary>
  );
};
